<template>
  <BaseDialog
    :open="open"
    :title="title"
    :max-width="'800px'"
    @close="onClose"
  >
    <template #title>
      {{ headerTitle }}
    </template>

    <form
      class="form"
      @submit.prevent="onSubmit"
    >
      <!-- Kopf / Stammdaten -->
      <div
        v-if="props.mode !== 'edit'"
        class="top-grid"
      >
        <div class="field mb-0">
          <label for="as-template">Vorlage</label>
          <select
            id="as-template"
            v-model.number="model.template_id"
            :class="['input', { error: hasErr('template_id') }]"
            :disabled="loadingTemplates"
            required
            @change="onTemplateChange"
          >
            <option
              :value="null"
              disabled
            >
              {{ loadingTemplates ? 'Vorlagen werden geladen…' : 'Bitte auswählen…' }}
            </option>
            <option
              v-for="t in templates"
              :key="t.id"
              :value="t.id"
            >
              {{ t.subject ? `${t.subject} – ` : '' }}{{ t.topic || t.title || `Vorlage #${t.id}` }}
            </option>
          </select>
          <p
            v-if="templatesError"
            class="help danger"
            role="alert"
          >
            {{ templatesError }}
          </p>
          <p
            v-if="hasErr('template_id')"
            class="help danger"
            role="alert"
          >
            {{ serverErrors.template_id }}
          </p>
        </div>

        <div class="field mb-0">
          <label for="as-student">Schüler</label>
          <select
            id="as-student"
            v-model.number="model.student_id"
            :class="['input', { error: hasErr('student_id') }]"
            :disabled="loadingStudents"
            required
          >
            <option
              :value="null"
              disabled
            >
              {{ loadingStudents ? 'Schülerliste wird geladen…' : 'Bitte auswählen…' }}
            </option>
            <option
              v-for="s in students"
              :key="s.id"
              :value="s.id"
            >
              {{ formatStudent(s) }}
            </option>
          </select>
          <p
            v-if="studentsError"
            class="help danger"
            role="alert"
          >
            {{ studentsError }}
          </p>
          <p
            v-if="hasErr('student_id')"
            class="help danger"
            role="alert"
          >
            {{ serverErrors.student_id }}
          </p>
        </div>
      </div>

      <div
        v-if="hasServerErrors"
        class="notice notice-danger mt-12"
        role="alert"
      >
        <strong>Eingaben ungültig:</strong>
        <ul class="errors-list">
          <li
            v-for="(msg, key) in serverErrors"
            :key="key"
          >
            <span class="key">{{ fieldLabel(key) }}:</span> <span class="msg">{{ msg }}</span>
          </li>
        </ul>
      </div>

      <!-- Raster -->
      <div
        v-if="templateNormalized"
        class="sheet"
      >
        <header class="sheet-header">
          <div>
            <p class="sheet-meta">
              <span
                v-if="templateMetaSubject"
                class="d-block"
              >
                <strong>Fach:</strong> {{ templateMetaSubject }}
              </span>
              <span
                v-if="templateMetaTopic"
                class="d-block"
              >
                <strong>Thema:</strong> {{ templateMetaTopic }}
              </span>
            </p>
          </div>
          <div
            v-if="diff.hasDiff"
            class="scale-hint"
          >
            <span
              v-if="diff.addedTotal > 0"
              class="pill chip-new"
            >+{{ diff.addedTotal }} neu</span>
            <span
              v-if="diff.removedTotal > 0"
              class="pill chip-removed"
            >−{{ diff.removedTotal }} entfernt</span>
            <span
              v-if="diff.changedTotal > 0"
              class="pill chip-changed"
            >~{{ diff.changedTotal }} geändert</span>
          </div>
        </header>

        <div class="sheet-grid">
          <!-- Links: Kompetenzen -->
          <section class="panel">
            <header class="panel-header">
              <h4 class="panel-title">
                Kompetenzen
              </h4>
              <span class="meta">{{ templateNormalized.grouped.groups.length }} Gruppen</span>
            </header>

            <div
              v-if="templateNormalized.grouped.groups.length === 0"
              class="empty"
            >
              In der ausgewählten Vorlage sind keine Kompetenzgruppen hinterlegt.
            </div>

            <article
              v-for="g in templateNormalized.grouped.groups"
              :key="g.id"
              class="block"
            >
              <div class="block-title">
                <span>
                  {{ g.title }}
                  <span
                    v-if="isRemovedCompGroup(g.id)"
                    class="pill chip-removed"
                  >Entfernt</span>
                </span>

                <span
                  v-if="isRemovedCompGroup(g.id)"
                  class="row-label-actions"
                >
                  <button
                    type="button"
                    class="mini-btn mini-btn-danger"
                    @click="applyRemovedCompGroup(g.id)"
                  >
                    Entfernen
                  </button>
                </span>
              </div>

              <div class="rows">
                <!-- Snapshot-Items -->
                <div
                  v-for="it in g.items"
                  :key="it.id"
                  :class="['row', compRowClass(g.id, it.id)]"
                >
                  <div class="row-label">
                    <span class="row-label-text">
                      {{ it.label }}
                      <span
                        v-if="isRemovedComp(g.id, it.id)"
                        class="pill chip-removed"
                      >Entfernt</span>
                      <span
                        v-else-if="isChangedComp(g.id, it.id)"
                        class="pill chip-changed"
                      >Geändert</span>
                    </span>
                    <span class="row-label-actions">
                      <button
                        v-if="isChangedComp(g.id, it.id)"
                        type="button"
                        class="mini-btn mini-btn-warn"
                        @click="applyChangedComp(g.id, it.id)"
                      >
                        Übernehmen
                      </button>
                      <button
                        v-if="isRemovedComp(g.id, it.id)"
                        type="button"
                        class="mini-btn mini-btn-danger"
                        @click="applyRemovedComp(g.id, it.id)"
                      >
                        Entfernen
                      </button>
                    </span>
                  </div>

                  <div class="row-ctrl">
                    <label class="absent">
                      <input
                        type="checkbox"
                        :checked="isAbsent(compKey(g.id, it.id))"
                        @change="toggleAbsent(compKey(g.id, it.id), $event)"
                      >
                      <span>abwesend</span>
                    </label>

                    <input
                      class="percent-input"
                      type="text"
                      inputmode="numeric"
                      autocomplete="off"
                      :disabled="isAbsent(compKey(g.id, it.id))"
                      :value="isAbsent(compKey(g.id, it.id)) ? '' : String(getLevel(compKey(g.id, it.id)))"
                      :data-percent="1"
                      aria-label="Prozentwert 0 bis 100"
                      title="Prozentwert 0 bis 100"
                      @input="onPercentInput(compKey(g.id, it.id), $event)"
                      @blur="commitPercent(compKey(g.id, it.id), $event)"
                      @keydown.enter.prevent="focusNextPercent($event)"
                    >

                    <div
                      class="bar"
                      role="img"
                      :aria-label="`Bewertung: ${getLevel(compKey(g.id, it.id))}%`"
                    >
                      <div
                        class="bar-fill"
                        :style="{ width: `${isAbsent(compKey(g.id, it.id)) ? 0 : getLevel(compKey(g.id, it.id))}%` }"
                      />
                    </div>
                  </div>
                </div>

                <!-- Neu hinzugekommene Items (aus aktueller Vorlage) -->
                <div
                  v-for="it in addedCompItemsByGroup[g.id] || []"
                  :key="`added_${it.id}`"
                  class="row row-added"
                >
                  <div class="row-label">
                    <span class="row-label-text">
                      {{ it.label }}
                      <span class="pill chip-new">Neu</span>
                    </span>
                    <span class="row-label-actions">
                      <button
                        type="button"
                        class="mini-btn mini-btn-success"
                        @click="applyAddedComp(g.id, it.id)"
                      >
                        Übernehmen
                      </button>
                    </span>
                  </div>

                  <div class="row-ctrl">
                    <!-- disabled preview controls -->
                    <label class="absent">
                      <input
                        type="checkbox"
                        disabled
                      >
                      <span>abwesend</span>
                    </label>
                    <input
                      class="percent-input"
                      type="text"
                      inputmode="numeric"
                      autocomplete="off"
                      disabled
                      value=""
                    >
                    <div
                      class="bar"
                      role="img"
                      aria-label="Bewertung: neu"
                    >
                      <div
                        class="bar-fill"
                        :style="{ width: '0%' }"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </article>

            <!-- Neue Gruppen (komplett neu in aktueller Vorlage) -->
            <article
              v-for="g in addedCompGroups"
              :key="`added_group_${g.id}`"
              class="block block-added"
            >
              <div class="block-title">
                <span>
                  {{ g.title }}
                  <span class="pill chip-new">Neu</span>
                </span>
                <button
                  type="button"
                  class="mini-btn mini-btn-success"
                  @click="applyAddedCompGroup(g.id)"
                >
                  Gruppe übernehmen
                </button>
              </div>

              <div class="rows">
                <div
                  v-for="it in g.items"
                  :key="`added_group_it_${it.id}`"
                  class="row row-added"
                >
                  <div class="row-label">
                    <span class="row-label-text">
                      {{ it.label }}
                      <span class="pill chip-new">Neu</span>
                    </span>
                    <span class="row-label-actions">
                      <button
                        type="button"
                        class="mini-btn mini-btn-success"
                        @click="applyAddedComp(g.id, it.id)"
                      >
                        Übernehmen
                      </button>
                    </span>
                  </div>

                  <div class="row-ctrl">
                    <label class="absent">
                      <input
                        type="checkbox"
                        disabled
                      >
                      <span>abwesend</span>
                    </label>
                    <input
                      class="percent-input"
                      type="text"
                      inputmode="numeric"
                      autocomplete="off"
                      disabled
                      value=""
                    >
                    <div
                      class="bar"
                      role="img"
                      aria-label="Bewertung: neu"
                    >
                      <div
                        class="bar-fill"
                        :style="{ width: '0%' }"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </section>
        </div>
      </div>

      <p
        v-else
        class="notice mt-12"
        role="status"
      >
        Bitte zuerst eine Vorlage auswählen, damit das Raster angezeigt werden kann.
      </p>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <div class="actions">
        <div class="align-self-start w-20rem">
          <input
            id="as-internal-note"
            v-model.trim="internalNote"
            class="input"
            placeholder="interne Notiz"
            title="Nur intern – wird nicht im Lernbrief angezeigt."
          >
        </div>
        <div class="align-self-end flex gap-2">
          <button
            type="button"
            class="btn"
            @click="onClose"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="saving || !model.template_id || !model.student_id"
          >
            {{ saving ? 'Speichern…' : 'Speichern' }}
          </button>
        </div>
      </div>
    </form>
  </BaseDialog>
</template>

<script setup>
import {computed, nextTick, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import {useAuthStore} from '@/stores/auth';
import {getTemplate, listTemplates} from '@/api/feedbackTemplates';
import {getLetterStudents} from '@/api/feedbackLetters';
import {listAssessments} from '@/api/feedbackAssessments';

// Anzeige: DD.MM.YYYY (altInput), gespeicherter Wert: YYYY-MM-DD
const props = defineProps({
  open: {type: Boolean, default: false},
  mode: {type: String, default: 'new'}, // 'new' | 'edit'
  initial: {type: Object, default: null},
  // Aktueller Lernbrief: Nur Templates dieses Lernbriefs anbieten
  letterId: {type: [Number, String], default: null},
});

const emit = defineEmits(['close', 'saved']);

const auth = useAuthStore();

const saving = ref(false);
const error = ref(null);

// Server-Validierung
const serverErrors = ref({});
const hasServerErrors = computed(() => Object.keys(serverErrors.value || {}).length > 0);

function hasErr(name) {
  return !!(serverErrors.value && serverErrors.value[name]);
}

function fieldLabel(key) {
  const map = {
    template_id: 'Vorlage',
    student_id: 'Schüler/in',
    assessed_on: 'Datum',
    title: 'Titel',
    user_id: 'Lehrkraft',
    assessment_json: 'Bewertung'
  };
  return map[key] || key;
}

function applyServerValidationErrors(fields) {
  serverErrors.value = fields || {};
  error.value = 'Bitte Eingaben prüfen.';
}

defineExpose({applyServerValidationErrors});

const loadingTemplates = ref(false);
const templatesError = ref(null);
const templates = ref([]);

const loadingStudents = ref(false);
const studentsError = ref(null);
const students = ref([]);

const loadingTemplateDetails = ref(false);
const templateDetailsError = ref(null);
const selectedTemplate = ref(null); // full template (inkl. template_json)
const templateSnapshot = ref(null); // gespeicherter Snapshot aus assessment_json (für Edit-Modus)
const headerTitle = computed(() => {
  if (props.mode === 'edit') {
    const name = selectedStudentName.value;
    return name ? `Kompetenzraster bearbeiten (${name})` : 'Kompetenzraster bearbeiten';
  }
  if (props.mode === 'duplicate') return 'Kompetenzraster duplizieren';
  return 'Kompetenzraster hinzufügen';
});

// Merkt sich die zuletzt aktive Vorlage im Dialog.
// Wenn sich die Vorlage im Edit-Modus ändert, überschreiben wir die Assessment-JSON (Snapshot+Values) mit der neuen Vorlage.
const lastTemplateId = ref(null);

const model = ref({
  id: null,
  user_id: null,
  template_id: null,
  student_id: null,
  assessed_on: new Date().toISOString().slice(0, 10),
});

/**
 * values:
 *  - key: string (comp:groupId:itemId | beh:itemId)
 *  - value: { absent: boolean, level: number }  level 0..100
 */
const values = ref({});
const title = computed(() => {
  if (props.mode === 'edit') return 'Kompetenzraster bearbeiten';
  if (props.mode === 'duplicate') return 'Kompetenzraster duplizieren';
  return 'Neues Kompetenzraster';
});

function onClose() {
  emit('close');
}

function formatStudent(s) {
  const override = String(s?.student_name_override ?? '').trim();
  if (override) return override;

  const ln = (s?.last_name ?? '').toString().trim();
  const fn = (s?.first_name ?? '').toString().trim();
  return [fn, ln].filter(Boolean).join(' ');
}

const selectedStudentName = computed(() => {
  const sid = Number(model.value?.student_id) || 0;
  if (sid > 0) {
    const s = (students.value || []).find(x => Number(x?.id) === sid);
    const fromList = formatStudent(s);
    if (fromList) return fromList;
  }

  // Fallback (falls im Edit-Modus Schülerliste noch nicht geladen ist,
  // aber initial evtl. Namen mitliefert)
  const init = props.initial || {};
  const ln = (init?.student_last_name ?? init?.last_name ?? '').toString().trim();
  const fn = (init?.student_first_name ?? init?.first_name ?? '').toString().trim();
  const fromInitial = [fn, ln].filter(Boolean).join(' ');
  return fromInitial || '';
});

function normalizeTemplateJson(input) {
  // Unterstützt: Objekt oder JSON-String
  let parsed = input;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      parsed = null;
    }
  }

  const tpl = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  const sections = Array.isArray(tpl.sections) ? tpl.sections : [];

  const grouped =
      sections.find(s => s?.type === 'grouped_competencies' || s?.key === 'fachkompetenz') || {groups: []};
  const behavior = sections.find(s => s?.type === 'behavior_items' || s?.key === 'verhalten') || {items: []};

  const groupedOut = {
    key: grouped.key ?? 'fachkompetenz',
    label: grouped.label ?? 'Kompetenzen',
    groups: Array.isArray(grouped.groups) ? grouped.groups : [],
  };

  groupedOut.groups = groupedOut.groups.map(g => ({
    id: g?.id ?? `group_${Math.random().toString(36).slice(2, 10)}`,
    title: (g?.title ?? '').toString(),
    items: Array.isArray(g?.items)
        ? g.items.map(it => ({
          id: it?.id ?? `comp_${Math.random().toString(36).slice(2, 10)}`,
          label: (it?.label ?? '').toString(),
        }))
        : [],
  }));

  const behaviorOut = {
    key: behavior.key ?? 'verhalten',
    label: behavior.label ?? 'Arbeits- und Sozialverhalten',
    items: Array.isArray(behavior.items) ? behavior.items : [],
  };

  behaviorOut.items = behaviorOut.items.map(it => ({
    id: it?.id ?? `beh_${Math.random().toString(36).slice(2, 10)}`,
    label: (it?.label ?? '').toString(),
  }));

  return {
    title: (tpl.title ?? 'aus Rückmeldungsbogen').toString(),
    grouped: groupedOut,
    behavior: behaviorOut,
  };
}

const templateNormalized = computed(() => {
  if (templateSnapshot.value) return templateSnapshot.value;
  if (!selectedTemplate.value) return null;
  return normalizeTemplateJson(selectedTemplate.value.template_json);
});

const templateMetaSubject = computed(() => (selectedTemplate.value?.subject ?? '').toString().trim());
const templateMetaTopic = computed(() => (selectedTemplate.value?.topic ?? selectedTemplate.value?.title ?? '').toString().trim());
computed(() => {
  const n = templateNormalized.value;
  if (!n) return '';
  return n.title || 'aus Rückmeldungsbogen';
});
// Normalisierte Fassungen für Snapshot und aktuelle Vorlage (nur für Diff-Zwecke)
const currentNormalized = computed(() => {
  if (!selectedTemplate.value) return null;
  return normalizeTemplateJson(selectedTemplate.value.template_json);
});

const snapshotNormalized = computed(() => {
  if (!templateSnapshot.value) return null;
  // snapshot ist bereits normalisiert beim Speichern
  return templateSnapshot.value;
});

// Key-Sets für schnellen Vergleich
function makeCompItemKeySet(norm) {
  const out = new Set();
  if (!norm) return out;
  norm.grouped?.groups?.forEach(g => {
    (g.items || []).forEach(it => out.add(`comp:${String(g.id)}:${String(it.id)}`));
  });
  return out;
}

function makeBehItemKeySet(norm) {
  const out = new Set();
  if (!norm) return out;
  (norm.behavior?.items || []).forEach(it => out.add(`beh:${String(it.id)}`));
  return out;
}

function makeCompGroupIdSet(norm) {
  const out = new Set();
  if (!norm) return out;
  (norm.grouped?.groups || []).forEach(g => out.add(String(g.id)));
  return out;
}

// Label-Maps für "changed" Erkennung (gleiche ID, anderer Text)
const currentCompLabelMap = computed(() => {
  const out = new Map();
  const curr = currentNormalized.value;
  if (!curr) return out;
  curr.grouped?.groups?.forEach(g => {
    (g.items || []).forEach(it => out.set(compKey(g.id, it.id), String(it.label ?? '').trim()));
  });
  return out;
});

const snapshotCompLabelMap = computed(() => {
  const out = new Map();
  const snap = snapshotNormalized.value;
  if (!snap) return out;
  snap.grouped?.groups?.forEach(g => {
    (g.items || []).forEach(it => out.set(compKey(g.id, it.id), String(it.label ?? '').trim()));
  });
  return out;
});

const currentBehLabelMap = computed(() => {
  const out = new Map();
  const curr = currentNormalized.value;
  if (!curr) return out;
  (curr.behavior?.items || []).forEach(it => out.set(behKey(it.id), String(it.label ?? '').trim()));
  return out;
});

const snapshotBehLabelMap = computed(() => {
  const out = new Map();
  const snap = snapshotNormalized.value;
  if (!snap) return out;
  (snap.behavior?.items || []).forEach(it => out.set(behKey(it.id), String(it.label ?? '').trim()));
  return out;
});

// Diff erweitern: changed (IDs vorhanden, Label geändert)
const diff = computed(() => {
  const snap = snapshotNormalized.value;
  const curr = currentNormalized.value;
  if (!snap || !curr) {
    return {
      hasDiff: false,
      comp: {added: new Set(), removed: new Set(), changed: new Set()},
      beh: {added: new Set(), removed: new Set(), changed: new Set()},
      groups: {removed: new Set()},
      addedTotal: 0,
      removedTotal: 0,
      changedTotal: 0,
    };
  }

  const snapComp = makeCompItemKeySet(snap);
  const currComp = makeCompItemKeySet(curr);
  const snapBeh = makeBehItemKeySet(snap);
  const currBeh = makeBehItemKeySet(curr);

  const addedComp = new Set([...currComp].filter(k => !snapComp.has(k)));
  const removedComp = new Set([...snapComp].filter(k => !currComp.has(k)));

  const addedBeh = new Set([...currBeh].filter(k => !snapBeh.has(k)));
  const removedBeh = new Set([...snapBeh].filter(k => !currBeh.has(k)));

  const changedComp = new Set();
  for (const k of snapComp) {
    if (!currComp.has(k)) continue;
    const a = snapshotCompLabelMap.value.get(k) ?? '';
    const b = currentCompLabelMap.value.get(k) ?? '';
    if (a !== b) changedComp.add(k);
  }

  const changedBeh = new Set();
  for (const k of snapBeh) {
    if (!currBeh.has(k)) continue;
    const a = snapshotBehLabelMap.value.get(k) ?? '';
    const b = currentBehLabelMap.value.get(k) ?? '';
    if (a !== b) changedBeh.add(k);
  }

  // NEU: entfernte Gruppen (im Snapshot vorhanden, in aktueller Vorlage nicht mehr)
  const snapGroups = makeCompGroupIdSet(snap);
  const currGroups = makeCompGroupIdSet(curr);
  const removedGroups = new Set([...snapGroups].filter(gid => !currGroups.has(gid)));

  const addedTotal = addedComp.size + addedBeh.size;
  const removedTotal = removedComp.size + removedBeh.size;
  const changedTotal = changedComp.size + changedBeh.size;

  return {
    hasDiff: addedTotal > 0 || removedTotal > 0 || changedTotal > 0 || removedGroups.size > 0,
    comp: {added: addedComp, removed: removedComp, changed: changedComp},
    beh: {added: addedBeh, removed: removedBeh, changed: changedBeh},
    groups: {removed: removedGroups},
    addedTotal,
    removedTotal,
    changedTotal,
  };
});

function isChangedComp(groupId, itemId) {
  const k = compKey(groupId, itemId);
  return !!diff.value?.comp?.changed?.has(k);
}

function compRowClass(groupId, itemId) {
  if (isRemovedComp(groupId, itemId)) return 'row-removed';
  if (isChangedComp(groupId, itemId)) return 'row-changed';
  return '';
}

function isRemovedCompGroup(groupId) {
  return !!diff.value?.groups?.removed?.has(String(groupId));
}

// Neu-Items tatsächlich in die Liste einblenden (aus aktuellem Template)
const addedCompItemsByGroup = computed(() => {
  const out = {};
  const curr = currentNormalized.value;
  if (!curr) return out;

  curr.grouped?.groups?.forEach(g => {
    const list = (g.items || [])
        .filter(it => diff.value?.comp?.added?.has(compKey(g.id, it.id)))
        .map(it => ({id: it.id, label: String(it.label ?? '').toString()}));
    if (list.length) out[g.id] = list;
  });

  return out;
});

const addedCompGroups = computed(() => {
  const curr = currentNormalized.value;
  const snap = snapshotNormalized.value;
  if (!curr || !snap) return [];

  const snapGroupIds = new Set((snap.grouped?.groups || []).map(g => String(g.id)));
  return (curr.grouped?.groups || [])
      .filter(g => !snapGroupIds.has(String(g.id)))
      .map(g => ({
        id: g.id,
        title: String(g.title ?? '').toString(),
        items: (g.items || []).map(it => ({id: it.id, label: String(it.label ?? '').toString()})),
      }));
});
computed(() => {
  const curr = currentNormalized.value;
  if (!curr) return [];
  return (curr.behavior?.items || [])
      .filter(it => diff.value?.beh?.added?.has(behKey(it.id)))
      .map(it => ({id: it.id, label: String(it.label ?? '').toString()}));
});

function cloneJson(val) {
  return val ? JSON.parse(JSON.stringify(val)) : val;
}

function ensureSnapshotEditable() {
  if (templateSnapshot.value) return;
  // Fallback: falls Snapshot nicht existiert (sollte im Edit-Fall aber existieren)
  templateSnapshot.value = cloneJson(templateNormalized.value);
}

function findCurrentGroup(groupId) {
  const curr = currentNormalized.value;
  if (!curr) return null;
  return (curr.grouped?.groups || []).find(g => String(g.id) === String(groupId)) || null;
}

function findSnapshotGroup(groupId) {
  const snap = templateSnapshot.value;
  if (!snap) return null;
  return (snap.grouped?.groups || []).find(g => String(g.id) === String(groupId)) || null;
}

function findCurrentCompItem(groupId, itemId) {
  const g = findCurrentGroup(groupId);
  if (!g) return null;
  return (g.items || []).find(it => String(it.id) === String(itemId)) || null;
}

function findSnapshotCompItem(groupId, itemId) {
  const g = findSnapshotGroup(groupId);
  if (!g) return null;
  return (g.items || []).find(it => String(it.id) === String(itemId)) || null;
}

function applyAddedCompGroup(groupId) {
  ensureSnapshotEditable();
  if (findSnapshotGroup(groupId)) return;

  const currG = findCurrentGroup(groupId);
  if (!currG) return;

  templateSnapshot.value.grouped.groups.push({
    id: currG.id,
    title: String(currG.title ?? '').toString(),
    items: [],
  });
}

function applyAddedComp(groupId, itemId) {
  ensureSnapshotEditable();

  const currIt = findCurrentCompItem(groupId, itemId);
  if (!currIt) return;

  let snapG = findSnapshotGroup(groupId);
  if (!snapG) {
    // Gruppe existiert noch nicht im Snapshot → zuerst anlegen (als eigener Schritt gibt es oben auch den Button)
    applyAddedCompGroup(groupId);
    snapG = findSnapshotGroup(groupId);
  }
  if (!snapG) return;

  const exists = (snapG.items || []).some(it => String(it.id) === String(itemId));
  if (exists) return;

  snapG.items.push({
    id: currIt.id,
    label: String(currIt.label ?? '').toString(),
  });

  // Optional: Werte-Objekt vorinitialisieren, damit Speichern/Migration konsistent bleibt
  ensureDefaultValue(compKey(groupId, itemId));
}

function applyChangedComp(groupId, itemId) {
  ensureSnapshotEditable();

  const currIt = findCurrentCompItem(groupId, itemId);
  const snapIt = findSnapshotCompItem(groupId, itemId);
  if (!currIt || !snapIt) return;

  snapIt.label = String(currIt.label ?? '').toString();
}

function applyRemovedComp(groupId, itemId) {
  ensureSnapshotEditable();

  const snapG = findSnapshotGroup(groupId);
  if (!snapG) return;

  snapG.items = (snapG.items || []).filter(it => String(it.id) !== String(itemId));

  const k = compKey(groupId, itemId);
  if (values.value && typeof values.value === 'object') {
    delete values.value[k];
  }
}

function applyRemovedCompGroup(groupId) {
  ensureSnapshotEditable();

  const snapG = findSnapshotGroup(groupId);
  if (!snapG) return;

  // Werte der Gruppe entfernen
  (snapG.items || []).forEach(it => {
    const k = compKey(groupId, it.id);
    if (values.value && typeof values.value === 'object') {
      delete values.value[k];
    }
  });

  // Gruppe aus Snapshot entfernen
  templateSnapshot.value.grouped.groups =
      (templateSnapshot.value.grouped.groups || []).filter(g => String(g.id) !== String(groupId));
}

function compKey(groupId, itemId) {
  return `comp:${String(groupId)}:${String(itemId)}`;
}

// Inline-Checks für entfernte Items
function isRemovedComp(groupId, itemId) {
  const k = compKey(groupId, itemId);
  return !!diff.value?.comp?.removed?.has(k);
}

function behKey(itemId) {
  return `beh:${String(itemId)}`;
}

function clampInt(val, min, max) {
  const n = Number.parseInt(String(val), 10);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, n));
}

function ensureDefaultValue(key) {
  if (!values.value[key]) values.value[key] = {absent: false, level: ''};
}

function isAbsent(key) {
  ensureDefaultValue(key);
  return !!values.value[key].absent;
}

function getLevel(key) {
  ensureDefaultValue(key);
  const lvl = clampInt(values.value[key].level, 0, 100);
  return lvl === null ? '' : lvl;
}

function toggleAbsent(key, e) {
  ensureDefaultValue(key);
  const checked = !!e?.target?.checked;
  values.value[key].absent = checked;
}

function onPercentInput(key, e) {
  // live: nur Ziffern durchlassen, aber noch nicht hart „clampen“, damit Tippen angenehm bleibt
  const el = e?.target;
  if (!el) return;
  const digits = String(el.value ?? '').replace(/[^\d]/g, '');
  el.value = digits;

  if (digits === '') return;
  const n = clampInt(digits, 0, 100);
  if (n === null) return;
  ensureDefaultValue(key);
  values.value[key].level = n;
}

function commitPercent(key, e) {
  // beim Verlassen: clampen + Feldwert normalisieren
  const el = e?.target;
  if (!el) return;

  const raw = String(el.value ?? '').trim();
  if (!raw) {
    // leer → auf 0 setzen (und visuell 0 eintragen)
    ensureDefaultValue(key);
    values.value[key].level = 0;
    el.value = '0';
    return;
  }

  const n = clampInt(raw, 0, 100);
  ensureDefaultValue(key);
  values.value[key].level = n === null ? 0 : n;
  el.value = String(values.value[key].level);
}

async function focusNextPercent(e) {
  const current = e?.target;
  if (!current) return;

  const all = Array.from(document.querySelectorAll('input[data-percent="1"]'));
  const idx = all.indexOf(current);
  if (idx < 0) return;

  const next = all[idx + 1];
  if (!next) return;

  await nextTick();
  next.focus();
  if (typeof next.select === 'function') next.select();
}

function seedValuesFromTemplateIfMissing() {
  const n = templateNormalized.value;
  if (!n) return;

  n.grouped.groups.forEach(g => {
    g.items.forEach(it => {
      const k = compKey(g.id, it.id);
      ensureDefaultValue(k);
    });
  });

  n.behavior.items.forEach(it => {
    const k = behKey(it.id);
    ensureDefaultValue(k);
  });
}

function parseMaybeJsonObject(input) {
  if (!input) return null;
  if (typeof input === 'object') return input;
  if (typeof input !== 'string') return null;

  try {
    const parsed = JSON.parse(input);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

async function loadDropdownData() {
  templatesError.value = null;
  studentsError.value = null;

  loadingTemplates.value = true;
  loadingStudents.value = true;

  try {
    // 1) Templates seitenweise laden (Backend limitiert auf max. 100)
    const collected = [];
    let page = 1;
    let pages = 1;

    do {
      const res = await listTemplates({page, limit: 100});
      const items = Array.isArray(res?.items) ? res.items : [];
      collected.push(...items);
      pages = Math.max(1, Number(res?.pages) || 1);
      page += 1;
    } while (page <= pages);

    // Filter: nur Templates des aktuellen Lernbriefs
    const lid = Number(props.letterId) || 0;
    let base = collected.filter(t => Number(t?.feedback_letter_id ?? 0) === lid);

    // Falls kein Admin: zusätzlich nur eigene (Creator) Templates
    const isAdmin = !!auth.isAdmin;
    if (!isAdmin) {
      const me = Number(auth.user?.id) || 0;
      base = base.filter(t => Number(t?.user_id ?? 0) === me);
    }

    templates.value = base;

    // Sicherstellen: aktuell ausgewählte Vorlage (Edit) ist in der Liste vorhanden
    const curTplId = Number(model.value?.template_id) || 0;
    if (curTplId > 0 && !templates.value.some(t => Number(t?.id) === curTplId)) {
      try {
        const curTpl = await getTemplate(curTplId);
        if (curTpl) {
          templates.value = [...templates.value, curTpl];
        }
      } catch {
        // still: wenn nicht ladbar, weiter mit vorhandener Liste
      }
    }

    // 2) Schüler aus Lernbrief laden
    let letterStudents = [];
    if (lid > 0) {
      const ls = await getLetterStudents(lid);
      letterStudents = Array.isArray(ls?.items) ? ls.items : [];
    }

    // 3) Bereits vergebene Raster mit der ausgewählten Vorlage (nur für diesen Lernbrief) einsammeln
    const selectedTplId = Number(model.value?.template_id) || 0;
    const usedStudentIds = new Set();
    if (lid > 0 && selectedTplId > 0) {
      let aPage = 1;
      let aPages = 1;
      do {
        const aRes = await listAssessments({page: aPage, limit: 100, feedbackLetterId: lid});
        const aItems = Array.isArray(aRes?.items) ? aRes.items : [];
        aItems.forEach(a => {
          const tId = Number(a?.template_id ?? a?.templateId) || 0;
          const sId = Number(a?.student_id ?? a?.studentId) || 0;
          if (tId === selectedTplId && sId > 0) {
            usedStudentIds.add(sId);
          }
        });
        aPages = Math.max(1, Number(aRes?.pages) || 1);
        aPage += 1;
      } while (aPage <= aPages);
    }

    // 4) Filtern: nur Schüler aus dem Lernbrief, ohne bereits bestehendes Raster zur ausgewählten Vorlage
    const keepId = Number(model.value?.student_id) || 0; // im Edit-Fall den aktuellen Schüler nicht herausfiltern
    const normalized = letterStudents.map(s => {
      const id = Number(s?.id ?? s?.student_id ?? 0) || 0;
      return {...s, id};
    });

    const filtered = normalized.filter(s => {
      const sid = Number(s.id) || 0;
      if (sid <= 0) return false;
      if (sid === keepId) return true;
      return !usedStudentIds.has(sid);
    });

    // Falls der aktuelle Schüler trotz Filterung nicht enthalten ist → hinzufügen
    if (keepId > 0 && !filtered.some(s => Number(s.id) === keepId)) {
      const keep = normalized.find(s => Number(s.id) === keepId);
      if (keep) filtered.push(keep);
    }

    // Sortierung wie bisher (Vorname, dann Nachname)
    filtered.sort((a, b) => {
      const aFn = (a?.first_name ?? '').toString().trim();
      const bFn = (b?.first_name ?? '').toString().trim();
      const byFirstName = aFn.localeCompare(bFn, 'de-DE', {sensitivity: 'base'});
      if (byFirstName !== 0) return byFirstName;

      const aLn = (a?.last_name ?? '').toString().trim();
      const bLn = (b?.last_name ?? '').toString().trim();
      return aLn.localeCompare(bLn, 'de-DE', {sensitivity: 'base'});
    });

    students.value = filtered;
  } catch (e) {
    templatesError.value = 'Vorlagen konnten nicht geladen werden.';
    studentsError.value = 'Schülerliste konnte nicht geladen werden.';
    console.error(e);
  } finally {
    loadingTemplates.value = false;
    loadingStudents.value = false;
  }
}

const internalNote = ref('');

function getTemplateInternalNote(tpl) {
  if (!tpl) return '';
  let t = tpl?.template_json;
  if (typeof t === 'string') {
    try {
      t = JSON.parse(t);
    } catch {
      t = null;
    }
  }
  return String(t?.internal_note ?? '').trim();
}

async function loadSelectedTemplateDetails(templateId) {
  templateDetailsError.value = null;
  selectedTemplate.value = null;

  if (!templateId) return;

  loadingTemplateDetails.value = true;
  try {
    selectedTemplate.value = await getTemplate(templateId);
    seedValuesFromTemplateIfMissing();

    // Default-Notiz (nur wenn noch nichts gesetzt ist – verhindert Überschreiben beim Nachladen)
    if (!String(internalNote.value || '').trim()) {
      internalNote.value = getTemplateInternalNote(selectedTemplate.value);
    }
  } catch (e) {
    templateDetailsError.value = 'Vorlage konnte nicht geladen werden.';
    console.error(e);
  } finally {
    loadingTemplateDetails.value = false;
  }
}

async function onTemplateChange() {
  const nextId = model.value.template_id;

  // Schülerauswahl sofort zurücksetzen und Liste leeren
  model.value.student_id = null;
  students.value = [];
  studentsError.value = null;
  loadingStudents.value = true;
  internalNote.value = '';

  await loadSelectedTemplateDetails(nextId);

  // Wenn ein bestehendes Assessment bearbeitet wird und die Vorlage wechselt:
  // assessment_json soll mit Vorlagen-Daten überschrieben werden.
  if (props.mode === 'edit' && String(nextId ?? '') !== String(lastTemplateId.value ?? '')) {
    templateSnapshot.value = null; // Snapshot verwerfen -> Anzeige/Save basiert auf aktueller Vorlage
    values.value = {}; // alte Werte entfernen
    seedValuesFromTemplateIfMissing(); // Default-Werte für neue Vorlage anlegen (0/leer)

    // Notiz ebenfalls auf neue Vorlage initialisieren
    internalNote.value = getTemplateInternalNote(selectedTemplate.value);
  }

  lastTemplateId.value = nextId ?? null;

  // Wichtig: Schülerliste neu laden (nur Lernbrief-Schüler, ohne bereits vorhandenes Raster für diese Vorlage)
  await loadDropdownData();
}

watch(
    () => props.open,
    async isOpen => {
      if (!isOpen) return;

      error.value = null;
      serverErrors.value = {};
      templateSnapshot.value = null;
      lastTemplateId.value = null;
      internalNote.value = '';

      if (props.mode === 'edit' && props.initial) {
        model.value = {
          id: props.initial.id ?? null,
          user_id: props.initial.user_id ?? null,
          template_id: props.initial.template_id ?? null,
          student_id: props.initial.student_id ?? null,
          assessed_on: props.initial.assessed_on ?? props.initial.date ?? new Date().toISOString().slice(0, 10),
        };

        const incoming = parseMaybeJsonObject(props.initial.assessment_json);

        if (incoming?.values && typeof incoming.values === 'object' && !Array.isArray(incoming.values)) {
          values.value = incoming.values;
        } else {
          values.value = {};
        }

        if (incoming?.template_snapshot && typeof incoming.template_snapshot === 'object') {
          templateSnapshot.value = incoming.template_snapshot;
        }

        internalNote.value = String(incoming?.internal_note ?? '').trim();
        lastTemplateId.value = model.value.template_id ?? null;
      } else if (props.mode === 'duplicate' && props.initial) {
        // Duplikat-Flow: wie "Neu", aber mit Vorbelegung aus dem Quell-Raster
        model.value = {
          id: null,
          user_id: auth.user?.id ?? null,
          template_id: props.initial.template_id ?? null,
          student_id: props.initial.student_id ?? null,
          assessed_on: props.initial.assessed_on ?? props.initial.date ?? new Date().toISOString().slice(0, 10),
        };

        const incoming = parseMaybeJsonObject(props.initial.assessment_json);

        if (incoming?.values && typeof incoming.values === 'object' && !Array.isArray(incoming.values)) {
          values.value = incoming.values;
        } else {
          values.value = {};
        }

        // Snapshot bewusst übernehmen (damit Werte/Struktur konsistent bleiben und Diff weiter funktioniert)
        if (incoming?.template_snapshot && typeof incoming.template_snapshot === 'object') {
          templateSnapshot.value = incoming.template_snapshot;
        } else {
          templateSnapshot.value = null;
        }

        internalNote.value = String(incoming?.internal_note ?? '').trim();

        lastTemplateId.value = model.value.template_id ?? null;
      } else {
        model.value = {
          id: null,
          user_id: auth.user?.id ?? null,
          template_id: null,
          student_id: null,
          assessed_on: new Date().toISOString().slice(0, 10),
        };
        values.value = {};
        lastTemplateId.value = null;
        internalNote.value = '';
      }

      // Aktuelle Vorlage laden
      if (model.value.template_id) {
        await loadSelectedTemplateDetails(model.value.template_id);

        // Falls "neu" und keine Notiz gesetzt → aus Vorlage übernehmen
        if (!String(internalNote.value || '').trim()) {
          internalNote.value = getTemplateInternalNote(selectedTemplate.value);
        }
      } else {
        selectedTemplate.value = null;
      }

      // Nach Setzen des Models (inkl. template_id) die Schülerliste gezielt laden/aktualisieren
      await loadDropdownData();
    },
    {immediate: true}
);

async function onSubmit() {
  error.value = null;
  serverErrors.value = {};

  if (!model.value.template_id) {
    error.value = 'Bitte eine Vorlage auswählen.';
    return;
  }
  if (!model.value.student_id) {
    error.value = 'Bitte eine/n Schüler/in auswählen.';
    return;
  }

  if (!selectedTemplate.value) {
    await loadSelectedTemplateDetails(model.value.template_id);
    if (!selectedTemplate.value) {
      error.value = templateDetailsError.value || 'Vorlage ist nicht verfügbar.';
      return;
    }
  }

  const payload = {
    user_id: model.value.user_id ?? (auth.user?.id ?? null),
    template_id: model.value.template_id,
    student_id: model.value.student_id,
    assessed_on: model.value.assessed_on,
    assessment_json: {
      template_snapshot: templateNormalized.value,
      values: values.value,
      internal_note: String(internalNote.value || '').trim(),
    },
  };

  saving.value = true;
  try {
    emit('saved', {mode: props.mode, id: model.value.id, payload});
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 82vh;
  max-height: 82vh;
  overflow: hidden; /* Scroll nur innerhalb .sheet */
}

.top-grid {
  display: grid;
  grid-template-columns: auto 200px;
  gap: 12px;
}

.field label {
  display: block;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 6px;
}

.input {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px;
  background: var(--surface);
  height: 36px;
}

.input.error {
  border-color: color-mix(in srgb, var(--danger) 60%, var(--border));
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--danger) 22%, transparent);
}

.errors-list {
  margin: 6px 0 0 0;
  padding-left: 18px;
}

.errors-list .key {
  font-weight: 500;
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin: 6px 0 0 0;
}

.help.danger {
  color: var(--danger);
}

.sheet {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  overflow-y: auto; /* vertikal scrollbar */
  overflow-x: hidden;
  flex: 1 1 auto; /* nimmt verfügbaren Platz ein */
  min-height: 0; /* wichtig für Scroll in Flex-Layouts */
}

.sheet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}

.sheet-title {
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: var(--text);
}

.sheet-meta {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
  display: inline-grid;
  gap: 3px;
}

.muted {
  color: var(--muted);
  font-size: 12px;
}

.scale-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.pill {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 2px 8px;
  background: var(--surface);
}

.chip-new {
  border-color: color-mix(in srgb, var(--success) 55%, var(--border));
  background: color-mix(in srgb, var(--success) 10%, var(--surface));
  color: var(--success);
  font-size: 12px;
}

.chip-removed {
  border-color: color-mix(in srgb, var(--danger) 55%, var(--border));
  background: color-mix(in srgb, var(--danger) 10%, var(--surface));
  color: var(--danger);
  font-size: 12px;
}

.sheet-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 12px;
}

.panel {
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--surface);
  min-width: 0;
}

.panel-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}

.panel-title {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
}

.meta {
  color: var(--muted);
  font-size: 12px;
}

.block {
  border-bottom: 1px solid var(--border);
}

.block:last-child {
  border-bottom: none;
}

.block-title {
  padding: 10px 12px;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
}

.rows {
  display: flex;
  flex-direction: column;
}

.row {
  display: grid;
  grid-template-columns: 1fr 240px;
  gap: 12px;
  padding: 0;
  border-bottom: 1px solid var(--border);
}

.row:last-child {
  border-bottom: none;
}

.row-label {
  font-size: 13px;
  line-height: 18px;
  color: var(--text);
  border-right: 1px solid var(--border);
  padding: 8px;
}

.rows.behavior .row {
  grid-template-columns: 1fr 130px;
}

.rows:not(.behavior) .row-label {
  padding: 8px 8px 8px 20px;
}


.row-ctrl {
  display: grid;
  grid-template-columns: 105px 1fr 80px;
  gap: 10px;
  align-items: center;
  justify-content: end;
  padding-right: 8px;
}

.row-ctrl-behavior {
  grid-template-columns: 1fr 80px;
}

.absent {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--muted);
  user-select: none;
}

.percent-input {
  height: 24px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  width: 34px;
  text-align: center;
  font-size: 13px;
}

.percent-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--focus);
  border-color: var(--primary);
}

.level {
  text-align: right;
  font-size: 12px;
  color: var(--muted);
}

.bar {
  height: 10px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--bg);
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: var(--success);
  width: 0%;
}

.empty {
  padding: 12px;
  color: var(--muted);
  font-size: 12px;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
  font-size: 14px;
}

.notice-danger {
  border-color: color-mix(in srgb, var(--danger) 45%, var(--border));
  background: color-mix(in srgb, var(--danger) 8%, var(--surface));
}

.actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
}

.btn {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 6px;
  cursor: pointer;
}

.btn-primary {
  border: 1px solid var(--primary);
  background: var(--primary);
  color: #fff;
}

.mt-12 {
  margin-top: 12px;
}

:deep(footer) {
  display: none;
}

.chip-changed {
  border-color: color-mix(in srgb, var(--warn) 55%, var(--border));
  background: color-mix(in srgb, var(--warn) 12%, var(--surface));
  color: var(--warn);
  font-size: 12px;
}

.row-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.row-label-text {
  min-width: 0;
}

.row-label-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.mini-btn {
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
  font-size: 12px;
}

.mini-btn:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.mini-btn-success {
  border-color: color-mix(in srgb, var(--success) 55%, var(--border));
  background: color-mix(in srgb, var(--success) 10%, var(--surface));
  color: var(--success);
}

.mini-btn-danger {
  border-color: color-mix(in srgb, var(--danger) 55%, var(--border));
  background: color-mix(in srgb, var(--danger) 10%, var(--surface));
  color: var(--danger);
}

.mini-btn-warn {
  border-color: color-mix(in srgb, var(--warn) 55%, var(--border));
  background: color-mix(in srgb, var(--warn) 12%, var(--surface));
  color: var(--warn);
}

.row-added {
  background: color-mix(in srgb, var(--success) 10%, var(--surface));
}

.row-removed {
  background: color-mix(in srgb, var(--danger) 10%, var(--surface));
}

.row-changed {
  background: color-mix(in srgb, var(--warn) 12%, var(--surface));
}

.block-added .block-title {
  background: color-mix(in srgb, var(--success) 8%, var(--surface));
}

@media (max-width: 1024px) {
  .top-grid {
    grid-template-columns: 1fr 1fr;
  }

  .sheet-grid {
    grid-template-columns: 1fr;
  }

  .row-ctrl {
    grid-template-columns: 110px 1fr 50px;
  }

  .row-ctrl-behavior {
    grid-template-columns: 1fr 70px 50px;
  }
}
</style>